import * as React from 'react';
import { UserActivationClient, SecurityClient } from 'mappClient/clients';
import { IAvailableLocalization, IUpdateUserSettings } from 'mappClient/models';
import { Util } from 'mappMvc/models';
import { Localization } from 'mappMvc/compile';
import { KeyCode } from 'mappMvc/enums';
import { UserSettingsPropertyType } from './../enums/userSettingsPropertyType';
import { ModalUtil } from 'mappMvc/models/modal';
import QRCode from 'qrcode';
import { MetadataClient } from 'mappClient/metadataClient';

interface IProps {
    userId: string;
    propertyType?: UserSettingsPropertyType;
    languageData?: { currentLanguage: IAvailableLocalization, data: Array<IAvailableLocalization> };
    close: (logout: boolean) => void;
    removeEventListener: () => void;
}

interface IState {
    currentPassword: string;
    currentLanguage: string;
    newEmail: string;
    newPW: string;
    newPWConfirm: string;
    secret: string;
    token: string;
    currentPasswordInvalid: boolean;
    currentPasswordMissing: boolean;
    newPasswordInvalid: boolean;
    newPasswordsDontMatch: boolean;
    newEmailInvalid: boolean;
}

export class UserSettingsPanelModalComponent extends React.Component<IProps, IState> {

    private canvasRef: React.RefObject<HTMLCanvasElement>;

    constructor(props: IProps) {

        super(props);

        this.state = {
            currentPassword: '',
            currentLanguage: this.props.languageData ? this.props.languageData.currentLanguage.name : undefined,
            newEmail: '',
            newPW: '',
            newPWConfirm: '',
            secret: '',
            token: '',
            currentPasswordInvalid: false,
            currentPasswordMissing: false,
            newPasswordsDontMatch: false,
            newPasswordInvalid: false,
            newEmailInvalid: false
        }

        this.props.removeEventListener();
        this.canvasRef = React.createRef<HTMLCanvasElement>();
    }

    getQrCode = async () => {
        if (this.props.propertyType === UserSettingsPropertyType.TwoFactor) {
            let result = await SecurityClient.requestTwoFactorAuthentication(this.props.userId);
            QRCode.toCanvas(this.canvasRef.current, result.url, {
                width: 250,
                height: 250,
                color: {
                    light: '#FFF'
                }
            });
            this.setState({
                secret: result.secret
            });
        }
    }

    save = async () => {

        let storage = localStorage.getItem(MetadataClient.storageId);
        let userLanguage = storage ? JSON.parse(storage).userLanguage : undefined;

        if (this.props.propertyType !== UserSettingsPropertyType.TwoFactor && this.props.propertyType !== UserSettingsPropertyType.TwoFactorRemoval) {
            if (this.state.currentPassword !== '') {
                let updateUserSettings: IUpdateUserSettings = {
                    currentPassword: this.state.currentPassword,
                    email: this.props.propertyType === UserSettingsPropertyType.Email ? this.state.newEmail : null,
                    newPassword: (this.props.propertyType === UserSettingsPropertyType.Password && !this.state.newPasswordInvalid) ? this.state.newPW : null,
                    languageCode: (this.props.propertyType === UserSettingsPropertyType.Language && this.props.languageData && userLanguage ? this.state.currentLanguage !== userLanguage : true) ? this.state.currentLanguage : null
                }
                if (updateUserSettings.newPassword) {
                    // new password validation
                    let passwordValidation = await UserActivationClient.isPasswordValid({ value: this.state.currentPassword });
                    if (!passwordValidation.isValid) {
                        this.setState({
                            newPasswordInvalid: true
                        });
                    } else {
                        this.updateUserSettings(updateUserSettings);
                    }
                } else if (updateUserSettings.email) {
                    // new email validation
                    let emailRegex: RegExp = new RegExp(/(?:[a-z0-9!#$%&' * +/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|'(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*')@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/g);
                    if (emailRegex.test(updateUserSettings.email)) {
                        this.updateUserSettings(updateUserSettings);
                    } else {
                        this.setState({
                            newEmailInvalid: true
                        });
                    }
                } else {
                    this.updateUserSettings(updateUserSettings);
                }
            } else {
                this.setState({
                    currentPasswordMissing: true
                });
            }
        } else if (this.props.propertyType === UserSettingsPropertyType.TwoFactor) {
            this.updateTwoFactorAuthenticationOptions();
        } else if (this.props.propertyType === UserSettingsPropertyType.TwoFactorRemoval) {
            this.removeTwoFactorAuthentication();
        }
    }

    private updateUserSettings = async (updateUserSettings: IUpdateUserSettings) => {
        let update = await SecurityClient.updateUserSettings(this.props.userId, updateUserSettings).catch(() => {
            this.setState({
                currentPasswordInvalid: true
            });
        });
        if (update) {
            this.props.close(true);
        }
    }

    private updateTwoFactorAuthenticationOptions = async () => {
        let update = await SecurityClient.confirmTwoFactorAuthentication(this.props.userId, {
            currentPassword: this.state.currentPassword,
            secret: this.state.secret,
            token: this.state.token
        }).catch((e) => {
            console.error(e);
        });
        if (update) {
            this.props.close(true);
        }
    }

    private removeTwoFactorAuthentication = async () => {
        let update = await SecurityClient.disableTwoFactorAuthentication(this.props.userId,
            { currentPassword: this.state.currentPassword, token: this.state.token }
        ).catch((e) => {
            console.error(e);
        });

        if (update) {
            this.props.close(true);
        }
    }

    handlePWInput = (e) => {
        this.setState({ currentPassword: e.target.value, currentPasswordInvalid: false, currentPasswordMissing: false });
    }

    handleEmailInput = (e) => {
        this.setState({ newEmail: e.target.value, newPasswordInvalid: false, newEmailInvalid: false });
    }

    handleNewPWInput = (e) => {
        if (this.state.newPWConfirm !== '') {
            if (e.target.value === this.state.newPWConfirm) {
                this.setState({ newPW: e.target.value, newPasswordsDontMatch: false, newPasswordInvalid: false, newEmailInvalid: false });
            } else {
                this.setState({ newPW: e.target.value, newPasswordsDontMatch: true, newPasswordInvalid: false, newEmailInvalid: false });
            }
        } else {
            this.setState({ newPW: e.target.value, newPasswordsDontMatch: false, newPasswordInvalid: false, newEmailInvalid: false });
        }
    }

    handleNewPWConfirmInput = (e) => {
        if (e.target.value === this.state.newPW) {
            this.setState({ newPWConfirm: e.target.value, newPasswordsDontMatch: false, newPasswordInvalid: false, newEmailInvalid: false });
        } else {
            this.setState({ newPWConfirm: e.target.value, newPasswordsDontMatch: true, newPasswordInvalid: false, newEmailInvalid: false });
        }
    }

    handleTokenInput = (e) => {
        this.setState({ token: e.target.value, newPasswordsDontMatch: false, newPasswordInvalid: false, newEmailInvalid: false });
    }

    handleLanguageSelection = (e) => {
        this.setState({ currentLanguage: e.target.value });
    }

    getTitle = (): string => {
        switch (this.props.propertyType) {
            case UserSettingsPropertyType.Email:
                return 'Change email';
            case UserSettingsPropertyType.Language:
                return 'Change language';
            case UserSettingsPropertyType.Password:
                return 'Change password';
            case UserSettingsPropertyType.TwoFactorRemoval:
                return Localization.Login.DisableTwoFactor;
            default:
                return Localization.Login.EnableTwoFactor;
        }
    }

    getLable = (): string => {
        switch (this.props.propertyType) {
            case UserSettingsPropertyType.Email:
                return Localization.Studio.User.EnterNewEmail;
            case UserSettingsPropertyType.Language:
                return Localization.Studio.User.EnterNewLanguage;
            case UserSettingsPropertyType.Password:
                return Localization.Login.NewPassword;
            case UserSettingsPropertyType.TwoFactorRemoval:
                return "";
            default:
                return Localization.Login.QrCode;
        }
    }

    getForm = (): React.ReactElement => {
        return <>
            <div className={`widget-wrapper mapp-cell-1-1 ${this.props.propertyType !== UserSettingsPropertyType.Password && 'mb-0'}`}>
                <label className='label mapp-cell-1-1' htmlFor='newPropertyValue'>{this.getLable()}</label>
                <div className='widget mapp-cell-1-1' title='This field is required.'>
                    {this.props.propertyType === UserSettingsPropertyType.Language ?

                        <select id='newPropertyValue' value={this.state.currentLanguage} onChange={this.handleLanguageSelection}>
                            {this.props.languageData.data.map((i, index) => { return <option key={index} value={i.name}>{i.displayName}</option> })}
                        </select>

                        :

                        this.props.propertyType === UserSettingsPropertyType.Email ?
                            <input className={this.state.newEmailInvalid ? 'err-border' : ''} id='newPropertyValue' type='email' value={this.state.newEmail} onChange={this.handleEmailInput}></input>

                            :

                            this.props.propertyType === UserSettingsPropertyType.Password &&
                            <input className={this.state.newPasswordInvalid || this.state.newPasswordsDontMatch ? 'err-border' : ''} id='newPropertyValue' type='password' value={this.state.newPW} onChange={this.handleNewPWInput}></input>

                    }
                    {this.state.newPasswordInvalid && <span className='err-text'>{Localization.Errors.ProvidedPasswordInvalid}</span>}
                    {this.state.newEmailInvalid && <span className='err-text'>{Localization.Errors.ProvidedEmailInvalid}</span>}
                </div>
            </div>
            {this.props.propertyType === UserSettingsPropertyType.Password &&
                <div className='widget-wrapper mapp-cell-1-1 mb-0'>
                    <label className='label mapp-cell-1-1' htmlFor='newPWConfirm'>{Localization.Login.NewPasswordConfirm}</label>
                    <div className='widget mapp-cell-1-1' title='This field is required.'>
                        <input className={this.state.newPasswordInvalid || this.state.newPasswordsDontMatch ? 'err-border' : ''} id='newPWConfirm' type='password' value={this.state.newPWConfirm} onChange={this.handleNewPWConfirmInput}></input>
                        {this.state.newPasswordsDontMatch && <span className='err-text'>{Localization.Errors.ProvidedPasswordsDontMatch}</span>}
                    </div>
                </div>
            }

            {((this.props.propertyType === UserSettingsPropertyType.TwoFactor) || (this.props.propertyType === UserSettingsPropertyType.TwoFactorRemoval)) &&
                <>
                    {(this.props.propertyType === UserSettingsPropertyType.TwoFactor) &&
                        <div className='widget-wrapper mapp-cell-1-1 mb-0 text-center' style={{ marginTop: "1rem" }}>
                            <canvas ref={this.canvasRef} id="qrcode" width="250" height="250"></canvas>
                        </div>
                    }
                    <div className='widget-wrapper mapp-cell-1-1' style={{ marginTop: "1rem", marginBottom: "1rem" }}>
                        <label className='label mapp-cell-1-1' htmlFor='token'>{Localization.Login.EnterToken}</label>
                        <div className='widget mapp-cell-1-1' title='This field is required.'>
                            <input id='token' type='text' value={this.state.token} onChange={this.handleTokenInput}></input>
                        </div>
                    </div>
                </>
            }
        </>;
    }

    getCurrentPWInput = (): React.ReactElement => {
        return <div className='widget-wrapper mapp-cell-1-1'>
            <label className='label mapp-cell-1-1' htmlFor='currentPassword'>{Localization.Studio.User.EnterCurrentPassword}</label>
            <div className='widget mapp-cell-1-1' title='This field is required.'>
                <input className={this.state.currentPasswordInvalid || this.state.currentPasswordMissing ? 'err-border' : ''} id='currentPassword' type='password' value={this.state.currentPassword} onChange={this.handlePWInput}></input>
                {this.state.currentPasswordInvalid && <span className='err-text'>{Localization.Errors.WrongPassword}</span>}
                {this.state.currentPasswordMissing && <span className='err-text'>{Localization.Errors.MissingPassword}</span>}
            </div>
        </div>;
    }

    componentDidMount = () => {
        Util.bindKey(KeyCode.ESCAPE, this.props.close, this);
        Util.bindKey(KeyCode.ENTER, this.save, this);
        ModalUtil.overlay(true);
        this.getQrCode();
    }

    componentWillUnmount = () => {
        Util.unbindKey(KeyCode.ESCAPE, this.props.close);
        Util.unbindKey(KeyCode.ENTER, this.save);
        ModalUtil.overlay(false);
    }

    render() {
        return (
            <div className='modal on react-modal-width-25'>
                <div className='header'>
                    <span>{this.getTitle()}</span>
                    <a onClick={() => { this.props.close(false) }}>x</a>
                </div>
                <div className='modal-content base-form-theme'>
                    <div className='modallist-wrapper'>
                        <div className='content mapp-row' style={{ paddingLeft: 15 + "px", paddingRight: 15 + "px" }}>
                            {this.props.propertyType !== UserSettingsPropertyType.TwoFactor && this.getCurrentPWInput()}
                            {this.getForm()}
                            {this.props.propertyType === UserSettingsPropertyType.TwoFactor && this.getCurrentPWInput()}
                        </div>
                        <div className='clear'></div>
                        <div className='action-bar action-theme-blue-bar align-right'>
                            <a className='action' onClick={() => { this.props.close(false) }}>{Localization.Cancel}</a>
                            <a className='action' onClick={() => this.save()}>{Localization.Apply}</a>
                        </div>
                    </div>
                </div>
            </div>

        )
    }
}

export default UserSettingsPanelModalComponent
